/**
 * useCancelPaymentState — le prélèvement des frais et ce qu'il répond (étape 4).
 *
 * Port littéral de `CancelPaymentScreen.js:91-104` (le POST, ses cinq champs), `:132-173`
 * (succès → accusé, 402 → « Paiement refusé »), `:175-188` (la confirmation avant de prélever) et
 * `:190-216` (les deux gestes de carte, qui ouvrent la MÊME page Stripe).
 *
 * Le POST lui-même est celui de l'étape 3 (`useCancelSubscriptionSubmit`) : ici le montant affiché
 * est celui qui vient d'être prélevé.
 */
import { useCallback, useMemo, useReducer } from "react"
import { useNavigation } from "@react-navigation/native"

import { toUICancelPaymentOutcome } from "../../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { type UICancelPaymentNotice, type UICancelPaymentStatus } from "../../../types"
import { type CancelFlowNavigation, type CancelFlowRouteParams } from "./cancelFlowNavigation"
import { useCancelSubscriptionSubmit } from "./useCancelSubscriptionSubmit"

/**
 * Le legacy concatène le suffixe à l'URL du portail (`CancelPaymentScreen.js:192` et `:215`) —
 * exactement ce que le portail Abonnement dénonce dans son commentaire
 * (`ParentSubscriptionPortalScreen.js:599-604`, où la session est créée au clic et le deep-link
 * passe par `flow_data`). Le comportement est repris tel quel : réparer la divergence Stripe
 * n'appartient pas à ce portage, elle est consignée dans `TRANSITION.md`.
 */
const PAYMENT_METHODS_PATH = "/payment-methods"

export type CancelPaymentState = {
    status: UICancelPaymentStatus
    notice: UICancelPaymentNotice | null
    confirmVisible: boolean
}

export type CancelPaymentAction =
    | { type: "OPEN_CONFIRM" }
    | { type: "CLOSE_CONFIRM" }
    | { type: "SUBMIT" }
    | { type: "OUTCOME"; status: UICancelPaymentStatus; notice: UICancelPaymentNotice }

export const initialCancelPaymentState: CancelPaymentState = {
    status: "idle",
    notice: null,
    confirmVisible: false,
}

/**
 * L'état du prélèvement. Exporté pour son test : c'est lui qui décide si le bouton tourne, si
 * l'alerte s'affiche, et de quelle couleur.
 */
export function cancelPaymentReducer(state: CancelPaymentState, action: CancelPaymentAction): CancelPaymentState {
    switch (action.type) {
        case "OPEN_CONFIRM":
            // Pendant un envoi, la question ne se repose pas (legacy `if (loading) return`).
            if (state.status === "submitting" || state.status === "authenticating") return state
            return { ...state, confirmVisible: true }
        case "CLOSE_CONFIRM":
            return { ...state, confirmVisible: false }
        case "SUBMIT":
            // Un nouvel essai efface le refus précédent : l'alerte parlerait du passé.
            return { status: "submitting", notice: null, confirmVisible: false }
        case "OUTCOME":
            return { ...state, status: action.status, notice: action.notice }
        default:
            return state
    }
}

export type UseCancelPaymentStateArgs = {
    routeParams: CancelFlowRouteParams
    /** Ce que la carte paie : le montant montré, envoyé et accusé. */
    dueCents: number
    endOfMonth: string | null
    billingPortalUrl: string | null
}

export function useCancelPaymentState(args: UseCancelPaymentStateArgs) {
    const { routeParams, dueCents, endOfMonth, billingPortalUrl } = args
    const navigation = useNavigation<CancelFlowNavigation>()
    const [state, dispatchUi] = useReducer(cancelPaymentReducer, initialCancelPaymentState)

    const submitCancel = useCancelSubscriptionSubmit({
        routeParams,
        dueCents,
        endOfMonth,
        parentEmail: routeParams.parent_email ?? null,
        onError: (error) => {
            const outcome = toUICancelPaymentOutcome(error)
            dispatchUi({ type: "OUTCOME", status: outcome.status, notice: outcome.notice })
        },
    })

    const onConfirm = useCallback(() => {
        dispatchUi({ type: "SUBMIT" })
        submitCancel()
    }, [submitCancel])

    /** Sans portail il n'y a rien à ouvrir : le rappel disparaît, donc le geste aussi. */
    const openBillingPortal = useMemo(
        () => (billingPortalUrl ? () => openExternalUrl(billingPortalUrl + PAYMENT_METHODS_PATH) : undefined),
        [billingPortalUrl]
    )

    const actions = useMemo(
        () => ({
            onPay: () => dispatchUi({ type: "OPEN_CONFIRM" }),
            onConfirm,
            onCancelConfirm: () => dispatchUi({ type: "CLOSE_CONFIRM" }),
            onUpdateCard: openBillingPortal,
            onUseAnotherCard: openBillingPortal,
            onBack: () => navigation.goBack(),
            onClose: () => navigation.popToTop(),
        }),
        [navigation, onConfirm, openBillingPortal]
    )

    return {
        paymentStatus: state.status,
        paymentNotice: state.notice,
        confirmVisible: state.confirmVisible,
        actions,
    }
}
